import { useEffect, useRef, useState } from 'react';
import { X, Package, TrendingUp, CalendarDays, Euro, Trash2, Sparkles } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import type {
    InventoryItem,
    InventoryItemDetail,
    StockHistoryPoint,
    MovementHistoryPoint,
    CrossCompanyRow,
} from '../../types/inventory';
import { StockHistoryChart } from './charts/StockHistoryChart';
import { MovementHistoryChart } from './charts/MovementHistoryChart';
import { CrossCompanyChart } from './charts/CrossCompanyChart';
import { InlineCommentInput } from './InlineCommentInput';

interface ArticleDetailDrawerProps {
    itemId: string;
    onClose: () => void;
    width: number;
    onWidthChange: (pct: number) => void;
    canEdit?: boolean;
    onToggleFlag?: (item: InventoryItem, flagType: 'ToBeDestroyed' | 'NewProduct') => void;
    onCommentAdded?: () => void;
    refreshKey?: number;
}

const MIN_WIDTH_PCT = 50;
const MAX_WIDTH_PCT = 85;

function formatNumber(num: number | null | undefined): string {
    if (num == null) return '-';
    return Math.round(num).toLocaleString('de-CH');
}

function formatEUR(num: number | null | undefined): string {
    if (num == null) return '-';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatPricePerKg(num: number | null | undefined): string | null {
    if (num == null) return null;
    return `${new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)} EUR/kg`;
}

function dohBadgeClass(doh: number | null): string {
    if (doh == null) return 'bg-gray-100 text-gray-600';
    if (doh < 180) return 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20';
    if (doh <= 365) return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20';
    return 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20';
}

export function ArticleDetailDrawer({ itemId, onClose, width, onWidthChange, canEdit = false, onToggleFlag, onCommentAdded, refreshKey = 0 }: ArticleDetailDrawerProps) {
    const [item, setItem] = useState<InventoryItemDetail | null>(null);
    const [stockHistory, setStockHistory] = useState<StockHistoryPoint[]>([]);
    const [movements, setMovements] = useState<MovementHistoryPoint[]>([]);
    const [crossCompany, setCrossCompany] = useState<CrossCompanyRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const dragStartX = useRef<number | null>(null);
    const dragStartWidth = useRef<number>(width);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.allSettled([
            inventoryApi.get(itemId),
            inventoryApi.getStockHistory(itemId),
            inventoryApi.getMovementHistory(itemId),
            inventoryApi.getCrossCompany(itemId),
        ]).then(([itemRes, stockRes, moveRes, ccRes]) => {
            if (cancelled) return;

            if (itemRes.status === 'fulfilled') {
                setItem(itemRes.value);
            } else {
                setError(itemRes.reason?.message || 'Failed to load item');
            }
            setStockHistory(stockRes.status === 'fulfilled' ? stockRes.value.history : []);
            setMovements(moveRes.status === 'fulfilled' ? moveRes.value.movements : []);
            setCrossCompany(ccRes.status === 'fulfilled' ? ccRes.value.rows : []);
            setLoading(false);
        });

        return () => { cancelled = true; };
    }, [itemId, refreshKey]);

    const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
        dragStartX.current = e.clientX;
        dragStartWidth.current = width;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    useEffect(() => {
        const handleMove = (e: MouseEvent) => {
            if (dragStartX.current == null) return;
            const deltaPx = dragStartX.current - e.clientX;
            const deltaPct = (deltaPx / window.innerWidth) * 100;
            const next = Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, dragStartWidth.current + deltaPct));
            onWidthChange(next);
        };
        const handleUp = () => {
            if (dragStartX.current != null) {
                dragStartX.current = null;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
    }, [onWidthChange]);

    return (
        <div
            className="sticky top-4 flex max-h-[calc(100vh-2rem)] flex-col self-start overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
            style={{ width: `${width}%` }}
        >
            {/* Drag handle on the left edge */}
            <div
                onMouseDown={handleDragStart}
                className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-blue-400/40"
                title="Drag to resize"
            />

            {/* Header */}
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-gray-900">
                        {item?.ProductName || (loading ? 'Loading…' : 'Article details')}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                        {item && (
                            <>
                                <span className="font-mono">{item.ProductCode}</span>
                                <span>·</span>
                                <span>{item.CompanyCode.toUpperCase()}</span>
                                <span>·</span>
                                <span>{item.WarehouseName}</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Action buttons (edit-only) */}
                {canEdit && item && (
                    <div className="relative flex items-center gap-1.5">
                        <button
                            onClick={() => onToggleFlag?.(item, 'ToBeDestroyed')}
                            className={`rounded p-1 transition-colors ${item.IsToBeDestroyed
                                ? 'bg-red-50 text-red-600 ring-1 ring-inset ring-red-600/20 hover:bg-red-100'
                                : 'text-gray-400 hover:bg-gray-100 hover:text-red-500'
                                }`}
                            title={item.IsToBeDestroyed ? 'Remove "To Be Destroyed"' : 'Mark "To Be Destroyed"'}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => onToggleFlag?.(item, 'NewProduct')}
                            className={`rounded p-1 transition-colors ${item.IsNewProduct
                                ? 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-600/20 hover:bg-blue-100'
                                : 'text-gray-400 hover:bg-gray-100 hover:text-blue-500'
                                }`}
                            title={item.IsNewProduct ? 'Remove "New Product"' : 'Mark "New Product"'}
                        >
                            <Sparkles className="h-4 w-4" />
                        </button>
                        <InlineCommentInput
                            itemId={item.ItemID}
                            onCommentAdded={() => onCommentAdded?.()}
                        />
                        <span className="mx-1 h-5 w-px bg-gray-200" />
                    </div>
                )}

                <button
                    onClick={onClose}
                    className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    title="Close details"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                {error && (
                    <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {/* KPI Header */}
                <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <CalendarDays className="h-3.5 w-3.5" />
                            Days on Hand
                        </div>
                        <div className={`mt-1.5 inline-flex rounded-md px-2 py-0.5 font-mono text-base font-semibold ${dohBadgeClass(item?.DaysOnHand ?? null)}`}>
                            {item?.DaysOnHand ?? '–'}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Package className="h-3.5 w-3.5" />
                            Stock Qty
                        </div>
                        <div className="mt-1 font-mono text-base font-semibold text-gray-900">
                            {formatNumber(item?.StockQuantity)}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <TrendingUp className="h-3.5 w-3.5" />
                            12M Sales
                        </div>
                        <div className="mt-1 font-mono text-base font-semibold text-gray-900">
                            {formatNumber(item?.Rolling12MSalesQty)}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <CalendarDays className="h-3.5 w-3.5" />
                            Last Sale
                        </div>
                        <div className="mt-1 text-base font-semibold text-gray-900">
                            {formatDate(item?.LatestSalesDate ?? null)}
                        </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Euro className="h-3.5 w-3.5" />
                            Stock Value
                        </div>
                        <div className="mt-1 font-mono text-base font-semibold text-gray-900">
                            €{formatEUR(item?.StockValueEUR)}
                        </div>
                        {formatPricePerKg(item?.PricePerKgEUR) && (
                            <div className="mt-0.5 font-mono text-xs text-gray-500">
                                ≈ {formatPricePerKg(item?.PricePerKgEUR)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Charts */}
                <section className="mb-5">
                    <h3 className="mb-2 text-sm font-semibold text-gray-800">
                        Stock Level — Last 36 Months
                    </h3>
                    <StockHistoryChart data={stockHistory} isLoading={loading} />
                </section>

                <section className="mb-5">
                    <h3 className="mb-2 text-sm font-semibold text-gray-800">
                        Inflow vs. Outflow — Last 36 Months
                    </h3>
                    <MovementHistoryChart data={movements} isLoading={loading} />
                </section>

                <section>
                    <h3 className="mb-2 text-sm font-semibold text-gray-800">
                        Cross-Company Comparison — 12M Sales
                    </h3>
                    <CrossCompanyChart data={crossCompany} isLoading={loading} />
                </section>
            </div>
        </div>
    );
}
